import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Available themes
export const THEMES = {
  light: 'light',
  dark: 'dark',
  grither: 'grither',
  custom: 'custom'
};

export const THEME_LABELS = {
  light: 'Светлая',
  dark: 'Темная', 
  grither: 'Grither',
  custom: 'Кастомная'
};

export const ThemeProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [currentTheme, setCurrentTheme] = useState('light');
  const [loading, setLoading] = useState(true);
  const [customThemes, setCustomThemes] = useState([]);
  const [activeCustomTheme, setActiveCustomTheme] = useState(null);

  // Fetch custom themes from backend
  const fetchCustomThemes = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/themes/custom`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const themes = data.themes || [];
        setCustomThemes(themes);
        
        // Check if there's an active custom theme
        const activeTheme = themes.find(theme => theme.is_active);
        if (activeTheme) {
          setActiveCustomTheme(activeTheme);
          
          // Если текущая тема custom, применим её
          if (currentTheme === 'custom') {
            applyCustomTheme(activeTheme);
          }
        } else {
          setActiveCustomTheme(null);
          
          // Если была выбрана кастомная тема, но активной нет - переключим на светлую
          if (currentTheme === 'custom') {
            changeTheme('light');
          }
        }
      }
    } catch (error) {
      console.log('Could not fetch custom themes:', error);
    }
  };

  const applyCustomTheme = (theme) => {
    if (!theme || !theme.colors) {
      console.warn('⚠️ Theme has no colors to apply', theme);
      return;
    }

    const root = document.documentElement;
    
    console.log('🎨 Applying custom theme:', theme.display_name);
    console.log('🎨 Theme colors:', theme.colors);
    console.log('🎨 Number of colors:', Object.keys(theme.colors).length);
    
    // Remove all standard theme classes first
    Object.values(THEMES).forEach(t => {
      root.classList.remove(t);
    });
    
    // Clear any existing CSS variables (comprehensive cleanup)
    const varsToRemove = [
      // Theme variables
      'theme-app-background', 'theme-card-background', 'theme-header-background', 'theme-modal-background',
      'theme-primary-text', 'theme-secondary-text', 'theme-muted-text', 'theme-heading-text',
      'theme-primary-button', 'theme-primary-button-hover', 'theme-secondary-button', 'theme-secondary-button-hover',
      'theme-danger-button', 'theme-danger-button-hover',
      'theme-input-background', 'theme-input-border', 'theme-input-focus-border', 'theme-input-text', 'theme-input-placeholder',
      'theme-border-color', 'theme-link-color', 'theme-success-color', 'theme-error-color',
      
      // Legacy variables for compatibility
      'app-background', 'card-background', 'header-background', 'modal-background',
      'primary-text', 'secondary-text', 'muted-text', 'heading-text',
      'primary-button', 'primary-button-hover', 'secondary-button', 'secondary-button-hover',
      'danger-button', 'danger-button-hover',
      'input-background', 'input-border', 'input-focus-border', 'input-text', 'input-placeholder',
      'border-color', 'link-color', 'success-color', 'error-color'
    ];
    
    varsToRemove.forEach(varName => {
      root.style.removeProperty(`--${varName}`);
    });
    
    console.log('🧹 Cleared existing theme variables');
    
    // Apply theme colors as CSS variables with --theme- prefix
    Object.entries(theme.colors).forEach(([key, value]) => {
      if (value && typeof value === 'string') {
        root.style.setProperty(`--theme-${key}`, value);
        // Also set without theme prefix for legacy compatibility
        root.style.setProperty(`--${key}`, value);
        console.log(`🎨 Set --theme-${key} = ${value}`);
      }
    });
    
    // Add custom theme class AFTER setting variables
    root.classList.add('custom');
    console.log('✅ Custom theme class added');
    
    // Force reflow to ensure styles are applied immediately
    requestAnimationFrame(() => {
      // Force a style recalculation
      const computedStyle = getComputedStyle(root);
      const testVar = computedStyle.getPropertyValue('--theme-app-background');
      console.log('🎨 Verified theme application, app-background:', testVar);
      
      // Trigger a small visual change to force repaint
      document.body.style.transform = 'translateZ(0)';
      requestAnimationFrame(() => {
        document.body.style.transform = '';
      });
    });
    
    console.log('🎨 Custom theme applied successfully:', theme.display_name);
  };

  useEffect(() => {
    const initializeTheme = async () => {
      try {
        console.log('🎨 Starting theme initialization...');
        
        // STEP 1: Always check system default theme first (public API, no auth needed)
        let systemDefaultTheme = 'light';
        let systemDefaultCustomTheme = null;
        
        try {
          console.log('🏛️ Fetching system default theme...');
          const systemResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/system/default-theme`);
          if (systemResponse.ok) {
            const systemData = await systemResponse.json();
            systemDefaultTheme = systemData.defaultTheme || 'light';
            systemDefaultCustomTheme = systemData.defaultCustomTheme;
            console.log('🏛️ System default theme:', systemDefaultTheme);
            if (systemDefaultCustomTheme) {
              console.log('🏛️ System default custom theme:', systemDefaultCustomTheme.display_name);
            }
          } else {
            console.log('⚠️ Could not fetch system default theme, using fallback');
          }
        } catch (error) {
          console.log('⚠️ Error fetching system default theme:', error);
        }
        
        // STEP 2: Check user's personal preference from localStorage AND backend if authenticated
        const userPreference = localStorage.getItem('theme');
        const userCustomThemeId = localStorage.getItem('selectedCustomThemeId');
        console.log('👤 User preference from localStorage:', userPreference);
        console.log('👤 User selected custom theme ID:', userCustomThemeId);
        
        // STEP 2.5: Try to get user's backend preferences if authenticated
        let backendUserTheme = null;
        let backendUserCustomThemeId = null;
        
        try {
          const token = localStorage.getItem('token');
          if (token) {
            console.log('🔐 Fetching user theme preferences from backend...');
            const userResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/theme-settings`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (userResponse.ok) {
              const userData = await userResponse.json();
              backendUserTheme = userData.userTheme;
              backendUserCustomThemeId = userData.userCustomThemeId;
              console.log('🔐 Backend user theme:', backendUserTheme);
              console.log('🔐 Backend user custom theme ID:', backendUserCustomThemeId);
            }
          }
        } catch (error) {
          console.log('⚠️ Could not fetch user backend preferences:', error);
        }
        
        // STEP 3: Determine which theme to use
        // 🎯 ИСПРАВЛЕНО: Пользовательский выбор из localStorage имеет АБСОЛЮТНЫЙ приоритет
        let themeToApply;
        let customThemeData = systemDefaultCustomTheme;
        let customThemeIdToApply = userCustomThemeId || backendUserCustomThemeId;
        
        if (userPreference) {
          // У пользователя есть localStorage настройка - используем её (АБСОЛЮТНЫЙ приоритет)
          themeToApply = userPreference;
          console.log('🎯 User has localStorage preference, using it with ABSOLUTE priority:', userPreference);
          
          // Для кастомных тем также приоритет на localStorage ID, а не backend
          if (userPreference === 'custom' && userCustomThemeId) {
            customThemeIdToApply = userCustomThemeId; // Приоритет на localStorage ID
            console.log('🎯 Using localStorage custom theme ID:', userCustomThemeId);
          }
        } else if (backendUserTheme) {
          // Нет localStorage, но есть backend настройка пользователя - используем её
          themeToApply = backendUserTheme;
          console.log('🎯 Using backend user preference:', backendUserTheme);
        } else {
          // Нет пользовательских настроек - используем системную домашнюю тему
          themeToApply = systemDefaultTheme;
          console.log('🎯 No user preference, using system default theme:', systemDefaultTheme);
          
          // Если системная домашняя тема кастомная, используем её данные
          if (systemDefaultTheme === 'custom' && systemDefaultCustomTheme) {
            customThemeIdToApply = systemDefaultCustomTheme.id;
            console.log('🏛️ Using system default custom theme:', systemDefaultCustomTheme.display_name);
          }
        }
        
        console.log('🎯 Final theme to apply:', themeToApply);
        
        // STEP 4: Apply the determined theme
        console.log('🎨 Applying theme:', themeToApply);
        
        if (themeToApply === 'custom') {
          // For custom themes, we need theme data
          let customThemeToApply = customThemeData;
          
          // If we don't have system custom theme data or user has specific custom theme preference, try to fetch custom themes
          if (!customThemeToApply || customThemeIdToApply) {
            console.log('🔄 Fetching custom themes from API...');
            try {
              const token = localStorage.getItem('token');
              const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/themes/custom`, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
              });
              
              if (response.ok) {
                const data = await response.json();
                const loadedThemes = data.themes || [];
                console.log('🎭 Loaded custom themes:', loadedThemes.length);
                
                // Update state
                setCustomThemes(loadedThemes);
                
                // If user has specific custom theme ID, try to find it first
                if (customThemeIdToApply) {
                  const userSelectedTheme = loadedThemes.find(theme => theme.id === customThemeIdToApply);
                  if (userSelectedTheme) {
                    console.log('🎯 Found user-selected custom theme:', userSelectedTheme.display_name);
                    customThemeToApply = userSelectedTheme;
                  } else {
                    console.log('⚠️ User-selected theme not found, falling back to active theme');
                  }
                }
                
                // If we still don't have a theme, find active or first available
                if (!customThemeToApply) {
                  customThemeToApply = loadedThemes.find(theme => theme.is_active) || loadedThemes[0];
                }
                
                if (customThemeToApply) {
                  console.log('🎨 Final custom theme to apply:', customThemeToApply.display_name);
                } else {
                  console.log('❌ No custom themes available, falling back to light');
                  themeToApply = 'light';
                }
              } else {
                console.log('❌ Failed to load custom themes, falling back to light');
                themeToApply = 'light';
              }
            } catch (error) {
              console.error('❌ Error loading custom themes:', error);
              themeToApply = 'light';
            }
          } else {
            // We have system default custom theme data, update state with it
            setCustomThemes([customThemeToApply]);
          }
          
          // Apply custom theme if we have the data
          if (customThemeToApply && themeToApply === 'custom') {
            setActiveCustomTheme(customThemeToApply);
            setCurrentTheme('custom');
            applyCustomTheme(customThemeToApply);
            
            // Ensure localStorage has the correct theme ID
            localStorage.setItem('selectedCustomThemeId', customThemeToApply.id);
          } else {
            // Fallback to light if custom theme failed
            setCurrentTheme('light');
            applyTheme('light');
            localStorage.setItem('theme', 'light');
            localStorage.removeItem('selectedCustomThemeId');
          }
        } else {
          // 🎯 ИСПРАВЛЕНО: Standard themes can be applied immediately
          console.log('🎨 Applying standard theme:', themeToApply);
          setCurrentTheme(themeToApply);
          applyTheme(themeToApply);
        }
        
      } catch (error) {
        console.error('❌ Error initializing theme:', error);
        // Fallback to light theme
        setCurrentTheme('light');
        applyTheme('light');
        localStorage.setItem('theme', 'light');
      } finally {
        setLoading(false);
      }
    };

    initializeTheme();
  }, []);

  const applyTheme = (theme) => {
    const root = document.documentElement;
    
    console.log('🎯 Applying theme:', theme);
    
    // Remove all theme classes first
    Object.values(THEMES).forEach(t => {
      root.classList.remove(t);
    });
    
    if (theme === 'custom' && activeCustomTheme) {
      // Apply custom theme
      applyCustomTheme(activeCustomTheme);
      return;
    }
    
    // For standard themes, simply add the theme class
    // The CSS variables are now defined in the CSS theme classes
    root.classList.add(theme);
    
    // Clear any lingering custom theme variables
    const customThemeVars = [
      'primary-bg', 'secondary-bg', 'card-bg', 'header-bg', 'tertiary-bg', 'modal-bg', 'footer-bg', 'sidebar-bg',
      'primary-text', 'secondary-text', 'accent-text', 'heading-text', 'tertiary-text', 'muted-text', 'placeholder-text',
      'primary-button', 'primary-button-hover', 'secondary-button', 'secondary-button-hover', 'primary-button-text', 'secondary-button-text',
      'danger-button', 'danger-button-hover', 'success-button', 'success-button-hover'
    ];
    
    customThemeVars.forEach(varName => {
      root.style.removeProperty(`--theme-${varName}`);
    });
    
    // Clear body background override from custom themes
    document.body.style.backgroundColor = '';
    
    console.log('✅ Applied standard theme class:', theme);
  };

  const changeTheme = (newTheme, customThemeId = null) => {
    console.log('🎭 Changing theme to:', newTheme, customThemeId ? `(custom theme ID: ${customThemeId})` : '');
    
    if (newTheme === 'custom') {
      console.log('🎨 Switching to custom theme...');
      
      // If specific theme ID is provided, try to find and apply that theme
      if (customThemeId && customThemes.length > 0) {
        const specificTheme = customThemes.find(theme => theme.id === customThemeId);
        if (specificTheme) {
          console.log('✅ Using specific custom theme:', specificTheme.display_name);
          setActiveCustomTheme(specificTheme);
          setCurrentTheme('custom');
          applyCustomTheme(specificTheme);
          
          // 🎯 ИСПРАВЛЕНО: Сохраняем в localStorage СРАЗУ И СИНХРОННО
          localStorage.setItem('theme', 'custom');
          localStorage.setItem('selectedCustomThemeId', customThemeId);
          console.log('💾 ✅ IMMEDIATELY saved custom theme to localStorage:', { theme: 'custom', customThemeId });
          return; // Exit early, we're done
        }
      }
      
      // Check if we already have custom themes and an active one (fallback behavior)
      if (customThemes.length > 0) {
        const activeTheme = customThemes.find(theme => theme.is_active) || customThemes[0];
        if (activeTheme) {
          console.log('✅ Using loaded custom theme (fallback):', activeTheme.display_name);
          setActiveCustomTheme(activeTheme);
          setCurrentTheme('custom');
          applyCustomTheme(activeTheme);
          
          // 🎯 ИСПРАВЛЕНО: Сохраняем в localStorage СРАЗУ И СИНХРОННО
          localStorage.setItem('theme', 'custom');
          localStorage.setItem('selectedCustomThemeId', activeTheme.id);
          console.log('💾 ✅ IMMEDIATELY saved fallback custom theme to localStorage:', { theme: 'custom', customThemeId: activeTheme.id });
          return; // Exit early, we're done
        }
      }
      
      // No themes loaded or no active theme, fetch fresh data
      console.log('🔄 Fetching custom themes from server...');
      const token = localStorage.getItem('token');
      
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/themes/custom`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        const freshThemes = data.themes || [];
        console.log('🎭 Fetched fresh themes:', freshThemes.length);
        
        // Update state
        setCustomThemes(freshThemes);
        
        // Try to find the specific theme first, then fallback to active theme
        let themeToApply = null;
        if (customThemeId) {
          themeToApply = freshThemes.find(theme => theme.id === customThemeId);
          console.log(customThemeId ? `🎯 Looking for specific theme ${customThemeId}:` : '🎯 No specific theme requested', themeToApply ? 'Found!' : 'Not found');
        }
        
        if (!themeToApply) {
          themeToApply = freshThemes.find(theme => theme.is_active) || freshThemes[0];
          console.log('🔄 Falling back to active/first theme:', themeToApply?.display_name);
        }
        
        if (themeToApply) {
          console.log('✅ Applying fetched theme:', themeToApply.display_name);
          setActiveCustomTheme(themeToApply);
          setCurrentTheme('custom');
          applyCustomTheme(themeToApply);
          
          // 🎯 ИСПРАВЛЕНО: Сохраняем в localStorage СРАЗУ И СИНХРОННО
          localStorage.setItem('theme', 'custom');
          localStorage.setItem('selectedCustomThemeId', themeToApply.id);
          console.log('💾 ✅ IMMEDIATELY saved fetched custom theme to localStorage:', { theme: 'custom', customThemeId: themeToApply.id });
        } else {
          console.log('❌ No custom theme available, switching to light');
          localStorage.setItem('theme', 'light');
          localStorage.removeItem('selectedCustomThemeId');
          setCurrentTheme('light');
          applyTheme('light');
        }
      })
      .catch(error => {
        console.error('Error fetching custom themes:', error);
        // Fallback to light theme
        console.log('🔄 Falling back to light theme due to error');
        localStorage.setItem('theme', 'light');
        localStorage.removeItem('selectedCustomThemeId');
        setCurrentTheme('light');
        applyTheme('light');
      });
      
    } else {
      // Standard themes - can apply immediately
      if (!Object.values(THEMES).includes(newTheme)) {
        console.error('Invalid theme:', newTheme);
        return;
      }

      console.log('🎨 Applying standard theme:', newTheme);
      setCurrentTheme(newTheme);
      applyTheme(newTheme);
      
      // 🎯 ИСПРАВЛЕНО: Сохраняем в localStorage СРАЗУ И СИНХРОННО
      localStorage.setItem('theme', newTheme);
      localStorage.removeItem('selectedCustomThemeId'); // Clear custom theme ID
      console.log('💾 ✅ IMMEDIATELY saved standard theme to localStorage:', newTheme);
    }
    
    // Save to backend if user is logged in (асинхронно, не блокируя localStorage)
    const token = localStorage.getItem('token');
    if (token) {
      const requestBody = { theme: newTheme };
      
      // Если выбрана кастомная тема, добавляем ID темы
      if (newTheme === 'custom') {
        const themeIdToSave = customThemeId || localStorage.getItem('selectedCustomThemeId') || (activeCustomTheme ? activeCustomTheme.id : null);
        if (themeIdToSave) {
          requestBody.custom_theme_id = themeIdToSave;
        }
      }
      
      console.log('💾 Saving theme to backend:', requestBody);
      
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/theme-settings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      }).catch(error => {
        console.log('Could not save theme preference to backend:', error);
      });
    }
  };

  const refreshCustomThemes = async () => {
    console.log('🔄 Refreshing custom themes...');
    
    try {
      // Получаем свежие данные с сервера
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/themes/custom`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const freshThemes = data.themes || [];
        console.log('🎭 Fresh themes from server:', freshThemes.length);
        
        // Обновляем состояние тем
        setCustomThemes(freshThemes);
        
        // Ищем активную тему
        const freshActiveTheme = freshThemes.find(theme => theme.is_active);
        
        if (freshActiveTheme) {
          console.log('🎭 Found fresh active theme:', freshActiveTheme.display_name);
          setActiveCustomTheme(freshActiveTheme);
          
          // Если текущая тема custom, применяем обновленную активную тему
          if (currentTheme === 'custom') {
            console.log('🎨 Applying refreshed active theme');
            applyCustomTheme(freshActiveTheme);
          }
        } else {
          console.log('⚠️ No active custom theme found after refresh');
          setActiveCustomTheme(null);
          
          // Если была выбрана кастомная тема, но активной нет - переключаем на светлую
          if (currentTheme === 'custom') {
            console.log('🔄 Switching to light theme as no active custom theme');
            changeTheme('light');
          }
        }
      } else {
        console.error('Failed to refresh themes:', response.status);
      }
    } catch (error) {
      console.error('Error refreshing custom themes:', error);
    }
  };

  const restoreGlobalTheme = () => {
    console.log('🔄 Restoring global theme:', currentTheme);
    if (currentTheme === 'custom' && activeCustomTheme) {
      applyCustomTheme(activeCustomTheme);
    } else {
      applyTheme(currentTheme);
    }
  };

  const resetToSystemTheme = async () => {
    console.log('🏛️ Resetting to system theme...');
    
    try {
      // Fetch current system default theme
      const systemResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/system/default-theme`);
      if (systemResponse.ok) {
        const systemData = await systemResponse.json();
        const systemDefaultTheme = systemData.defaultTheme || 'light';
        const systemDefaultCustomTheme = systemData.defaultCustomTheme;
        
        console.log('🏛️ System default theme to reset to:', systemDefaultTheme);
        
        // Clear user preference from localStorage - КЛЮЧЕВОЕ ИЗМЕНЕНИЕ
        localStorage.removeItem('theme');
        localStorage.removeItem('selectedCustomThemeId');
        
        // Apply system theme
        if (systemDefaultTheme === 'custom' && systemDefaultCustomTheme) {
          console.log('🎨 Resetting to system custom theme:', systemDefaultCustomTheme.display_name);
          setCustomThemes([systemDefaultCustomTheme]);
          setActiveCustomTheme(systemDefaultCustomTheme);
          setCurrentTheme('custom');
          applyCustomTheme(systemDefaultCustomTheme);
        } else {
          console.log('🎨 Resetting to system standard theme:', systemDefaultTheme);
          setCurrentTheme(systemDefaultTheme);
          applyTheme(systemDefaultTheme);
        }
        
        // НЕ сохраняем в localStorage - пусть остается пустым для системной темы
        console.log('🎯 Theme preferences cleared - will use system default on next login');
        
        return {
          success: true,
          systemTheme: systemDefaultTheme,
          customTheme: systemDefaultCustomTheme
        };
      } else {
        throw new Error('Failed to fetch system theme');
      }
    } catch (error) {
      console.error('❌ Error resetting to system theme:', error);
      return { success: false, error: error.message };
    }
  };

  // Expose function to window for ThemeDesigner
  useEffect(() => {
    window.refreshCustomThemes = refreshCustomThemes;
    window.changeTheme = changeTheme;
    window.restoreGlobalTheme = restoreGlobalTheme;
    window.resetToSystemTheme = resetToSystemTheme;
    window.activeCustomTheme = activeCustomTheme;
    window.currentTheme = currentTheme;
    
    return () => {
      delete window.refreshCustomThemes;
      delete window.changeTheme;
      delete window.restoreGlobalTheme;
      delete window.resetToSystemTheme;
      delete window.activeCustomTheme;
      delete window.currentTheme;
    };
  }, [currentTheme, activeCustomTheme]);

  useEffect(() => {
    // Глобальная функция для обновления тем
    window.refreshCustomThemes = refreshCustomThemes;
    window.changeTheme = changeTheme;
    
    return () => {
      delete window.refreshCustomThemes;
      delete window.changeTheme;
    };
  }, [customThemes, activeCustomTheme]); // Добавляем правильные зависимости

  // Load custom themes after authentication
  useEffect(() => {
    if (isAuthenticated) {
      console.log('🔐 User authenticated, loading custom themes...');
      fetchCustomThemes();
    } else {
      // 🎯 ИСПРАВЛЕНО: НЕ сбрасываем пользовательские настройки тем при разлогинивании
      // Пользователь может выбрать тему и она должна сохраняться независимо от авторизации
      console.log('🔓 User logged out, but keeping user theme preferences intact');
      
      // Только очищаем кастомные темы из state, но НЕ трогаем localStorage
      setCustomThemes([]);
      setActiveCustomTheme(null);
      
      // Если текущая тема была кастомной, переключаем на стандартную из localStorage или светлую
      if (currentTheme === 'custom') {
        const fallbackTheme = localStorage.getItem('theme') || 'light';
        if (fallbackTheme !== 'custom') {
          console.log('🔄 Switching from custom to fallback theme:', fallbackTheme);
          setCurrentTheme(fallbackTheme);
          applyTheme(fallbackTheme);
        } else {
          // Если в localStorage тоже custom, переключаем на light
          console.log('🔄 Switching from custom to light theme (no custom themes available)');
          setCurrentTheme('light');
          applyTheme('light');
          localStorage.setItem('theme', 'light');
          localStorage.removeItem('selectedCustomThemeId');
        }
      }
    }
  }, [isAuthenticated]);

  // Legacy support for components using isDarkMode
  const isDarkMode = currentTheme === 'dark';
  
  // Legacy toggle function
  const toggleTheme = () => {
    const nextTheme = currentTheme === 'light' ? 'dark' : 'light';
    changeTheme(nextTheme);
  };

  const value = {
    // New multi-theme API
    currentTheme,
    changeTheme,
    themes: THEMES,
    themeLabels: THEME_LABELS,
    
    // Custom themes API
    customThemes,
    activeCustomTheme,
    refreshCustomThemes,
    
    // System theme utilities
    resetToSystemTheme,
    
    // Legacy API for backward compatibility
    isDarkMode,
    loading,
    toggleTheme,
    theme: currentTheme,
    
    // Helper functions
    isGritherTheme: currentTheme === 'grither'
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};